import _defineProperty from "@babel/runtime/helpers/defineProperty";

function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); if (enumerableOnly) symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; }); keys.push.apply(keys, symbols); } return keys; }

function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i] != null ? arguments[i] : {}; if (i % 2) { ownKeys(Object(source), true).forEach(function (key) { _defineProperty(target, key, source[key]); }); } else if (Object.getOwnPropertyDescriptors) { Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)); } else { ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } } return target; }

import { keyframes } from '@emotion/core';
import { B100, N20, N200, N30, N800, skeleton as skeletonColor, subtleHeading, subtleText } from '@atlaskit/theme/colors';
import { borderRadius, fontSize, gridSize as gridSizeFn, skeletonShimmer } from '@atlaskit/theme/constants';
import { headingSizes } from '@atlaskit/theme/typography';
var gridSize = gridSizeFn();
var itemElemSpacing = gridSize * 1.5;
var itemExpectedElemSize = gridSize * 3;
var itemTopBottomPadding = gridSize;
var itemSidePadding = gridSize * 2.5;
var itemDescriptionSpacing = gridSize * 0.375;
var itemMinHeight = gridSize * 5;
var itemContentMinHeight = itemMinHeight - itemTopBottomPadding * 2;
var itemHeadingContentHeight = headingSizes.h100.lineHeight;
var itemHeadingFontSize = headingSizes.h100.size;
var skeletonItemElemSize = gridSize * 2.5;
var itemElemSkeletonOffset = (itemExpectedElemSize - skeletonItemElemSize) / 2;
var skeletonTextBorderRadius = 100;
var skeletonHeadingHeight = gridSize;
var skeletonContentHeight = 9;
var buttonOverrides = {
  backgroundColor: 'transparent',
  border: 0,
  outline: 0,
  margin: 0,
  width: '100%'
};
var anchorOverrides = {};
var customItemOverrides = {
  color: 'currentColor'
};
var disabledStyles = {
  cursor: 'not-allowed',
  '&, &:hover, &:focus, &:active': {
    backgroundColor: 'transparent',
    color: N200
  }
};
var selectedStyles = {
  backgroundColor: N20,
  textDecoration: 'none'
};
var shimmer = skeletonShimmer();
var shimmerKeyframes = keyframes(shimmer.keyframes);

var baseItemCSS = function baseItemCSS(isDisabled, isSelected) {
  return _objectSpread(_objectSpread({
    padding: "".concat(itemTopBottomPadding, "px ").concat(itemSidePadding, "px"),
    cursor: 'pointer',
    fontSize: fontSize(),
    // IE11 fix - wrapping container needs to be flex as well for vertical centering to work.
    display: 'flex',
    boxSizing: 'border-box',
    color: N800,
    userSelect: 'none',
    '&:visited': {
      color: N800
    },
    '&:hover': {
      color: N800,
      backgroundColor: N20,
      textDecoration: 'none'
    },
    '&:focus': {
      outline: 'none',
      boxShadow: isDisabled ? 'none' : "".concat(B100, " 0 0 0 2px inset")
    },
    '&:active': {
      boxShadow: 'none',
      color: N800,
      backgroundColor: N30
    },
    '::-moz-focus-inner': {
      border: 0
    }
  }, isSelected && selectedStyles), isDisabled && disabledStyles);
};

export var buttonItemCSS = function buttonItemCSS(isDisabled, isSelected) {
  return _objectSpread(_objectSpread({}, buttonOverrides), baseItemCSS(isDisabled, isSelected));
};
export var itemCSS = baseItemCSS;
export var contentCSS = {
  flexGrow: 1,
  textAlign: 'left',
  overflow: 'hidden',
  outline: 'none',
  display: 'flex',
  flexDirection: 'column',
  // Fix - avoid clipped text descenders when using standard 16px line-height
  lineHeight: 1.22
};
export var truncateCSS = {
  display: 'block',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap'
};
export var elemBeforeCSS = {
  display: 'flex',
  flexShrink: 0,
  marginRight: itemElemSpacing
};
export var elemAfterCSS = {
  display: 'flex',
  flexShrink: 0,
  marginLeft: itemElemSpacing
};
export var descriptionCSS = _objectSpread(_objectSpread({}, truncateCSS), {}, {
  color: subtleText(),
  marginTop: itemDescriptionSpacing,
  fontSize: headingSizes.h200.size
});
export var contentCSSWrapper = {
  display: 'flex',
  minHeight: itemContentMinHeight,
  alignItems: 'center',
  // IE11 fix - used with flex above to fix vertical centering.
  width: '100%'
};
export var linkItemCSS = function linkItemCSS(isDisabled, isSelected) {
  return _objectSpread(_objectSpread({}, anchorOverrides), baseItemCSS(isDisabled, isSelected));
};
export var customItemCSS = function customItemCSS(isDisabled, isSelected) {
  return _objectSpread(_objectSpread({}, customItemOverrides), baseItemCSS(isDisabled, isSelected));
};
export var itemHeadingCSS = {
  textTransform: 'uppercase',
  fontSize: itemHeadingFontSize,
  lineHeight: itemHeadingContentHeight / itemHeadingFontSize,
  fontWeight: 700,
  color: subtleHeading(),
  padding: "0 ".concat(itemSidePadding, "px")
};
export var skeletonHeadingItemCSS = function skeletonHeadingItemCSS(width, isShimmering) {
  return _objectSpread(_objectSpread({}, itemHeadingCSS), {}, {
    '&::after': _objectSpread(_objectSpread({
      // This renders the skeleton heading "text".
      backgroundColor: skeletonColor()
    }, isShimmering && _objectSpread(_objectSpread({}, shimmer.css), {}, {
      animationName: "".concat(shimmerKeyframes)
    })), {}, {
      height: skeletonHeadingHeight,
      width: width || '30%',
      borderRadius: skeletonTextBorderRadius,
      display: 'block',
      content: '""'
    })
  });
};
export var itemSkeletonCSS = function itemSkeletonCSS(hasAvatar, hasIcon, width, isShimmering) {
  return _objectSpread(_objectSpread(_objectSpread(_objectSpread({}, itemCSS(false, false)), {}, {
    pointerEvents: 'none',
    display: 'flex',
    alignItems: 'center',
    minHeight: itemMinHeight
  }, !width && {
    '&:nth-child(1n)::after': {
      flexBasis: '70%'
    },
    '&:nth-child(2n)::after': {
      flexBasis: '50%'
    },
    '&:nth-child(3n)::after': {
      flexBasis: '60%'
    },
    '&:nth-child(4n)::after': {
      flexBasis: '90%'
    },
    '&:nth-child(5n)::after': {
      flexBasis: '35%'
    },
    '&:nth-child(6n)::after': {
      flexBasis: '77%'
    }
  }), (hasAvatar || hasIcon) && {
    '&::before': _objectSpread(_objectSpread({
      // This will render a skeleton in the "elemBefore" position.
      content: '""',
      backgroundColor: skeletonColor()
    }, isShimmering && _objectSpread(_objectSpread({}, shimmer.css), {}, {
      animationName: "".concat(shimmerKeyframes)
    })), {}, {
      marginRight: itemElemSpacing + itemElemSkeletonOffset,
      width: skeletonItemElemSize,
      height: skeletonItemElemSize,
      marginLeft: itemElemSkeletonOffset,
      borderRadius: hasAvatar ? '100%' : borderRadius(),
      flexShrink: 0
    })
  }), {}, {
    '&::after': _objectSpread(_objectSpread({
      // This will render the skeleton "text".
      content: '""',
      backgroundColor: skeletonColor()
    }, isShimmering && _objectSpread(_objectSpread({}, shimmer.css), {}, {
      animationName: "".concat(shimmerKeyframes)
    })), {}, {
      // This is a little bespoke but we need to push everything down 1px
      //  because the skeleton content should align to the bottom of the text.
      // Confirm VR test failures before accepting a change.
      marginTop: 1,
      height: skeletonContentHeight,
      borderRadius: skeletonTextBorderRadius,
      flexBasis: width || '100%'
    })
  });
};